import {Logger} from '@app/logger.service';
import {ActivatedRoute} from '@angular/router';
import {SelectItem} from 'primeng/api';
import {Option} from '@shared/helper/formly-helpers';
import {addDays, differenceInCalendarDays, format, isDate, isToday, isWeekend, lightFormat, startOfDay} from 'date-fns';
import {environment} from '@env/environment';
import packageJson from '../../../../package.json';
import {isEqual, isNumber} from 'lodash-es';
import {formatInTimeZone} from 'date-fns-tz';
import {DateTime} from 'luxon';

const log = new Logger('helpers');

export type DateRange = [from: Date, to: Date];

export function breadcrumb(label: string, alias = false) {
    return alias ? {breadcrumb: {alias}} : {breadcrumb: {label}} ;
}

export function createDateRange(from: number, to: number): DateRange {
    const today = startOfDay(new Date());
    return [addDays(today, from), addDays(today, to)];
}

export interface HeaderItem {
    date: Date;
    label: string;
    dow: string;
    isToday: boolean;
    isWeekEnd: boolean;
}

export const appVersion = `${packageJson.version}` + (environment.production ? '' : '-dev');

export function verify(expression: any, waringMessage: string) {
    if (!expression) {
        throw new Error(waringMessage);
    }
}

export function thaiDate(localDate: string | Date): Date {
    if (typeof localDate === 'string') {
        return DateTime.fromISO(localDate, {zone: 'Asia/Bangkok'}).toJSDate();
    }else {
        return localDate;
    }
}

export function formatInThaiZone(date: Date | string | number, formatStr = 'yyyy-MM-dd HH:mm:ss') {
    return date ? formatInTimeZone(date, '+07:00', formatStr) : '';
}

export function trueFn(): boolean {
    return true;
}

export function midNight(date = new Date()) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function firstNonNull(...args: (string)[]): string {
    for (const arg of args) {
        if (arg) {
            return arg;
        }
    }
    return '-';
}

export function toDate(date: number | Date): Date {
    if (typeof date === 'number') {
        return new Date(date);
    } else {
        return date;
    }
}

export function isDateBetween(now: Date, from: Date | number, to: Date | number) {
    const nowTimestamp = now.getTime();

    let fromTimestamp: number;
    if (isDate(from)) {
        // @ts-ignore
        fromTimestamp = from.getTime();
    } else if (isNumber(from)) {
        fromTimestamp = from;
    } else {
        throw new Error('Invalid type for "from" parameter');
    }

    let toTimestamp: number;
    if (isDate(to)) {
        // @ts-ignore
        toTimestamp = to.getTime();
    } else if (isNumber(to)) {
        toTimestamp = to;
    } else {
        throw new Error('Invalid type for "to" parameter');
    }

    return nowTimestamp >= fromTimestamp && nowTimestamp <= toTimestamp;
}

export const comma = (n: number) => n ? n.toLocaleString() : '-';

export function noopFN(): void {
    alert('Function not yet implemented');
    // tslint:disable-next-line:no-console
    console.trace('noop function call');
}

export function parseTimeString(timeString: string): string {
    const time = timeString.match(/\d+/g);
    const hours = (time[0] || 0).toString().padStart(2, '0');
    const minutes = (time[1] || 0).toString().padStart(2, '0');
    return `${hours}:${minutes}`;
}

export function dateArray(from: Date | number, to: Date | number): Date[] {
    const results = [];
    const dayDiff = differenceInCalendarDays(to, from);

    for (let i = 0; i <= dayDiff; i++) {
        results.push(addDays(from, i));
    }

    return results;
}

export function finestError(e: any): string {
    log.error('finestError', (typeof e), e);
    if (typeof e === 'string') {
        if (e.startsWith('Fault') && e.includes('msg=')) {
            const msgIndex = e.indexOf('msg=');
            const msgEndIndex = e.indexOf(',', msgIndex);
            return e.substring(msgIndex + 4, msgEndIndex);
        }

        return e;
    }

    if (e?.error?.detail) {
        console.log('error---->', e);
        return e.error.detail;
    }

    if (e?.cause) {
        return extractErrorString(e.cause);
    }

    if (e?.error?.cause) {
        return extractErrorString(e.error.cause);
    }


    if (e?.status) {
        return `status: ${e.status}, ${e.message}`;
    }
    return e?.toString() ?? 'UnknownError';
}


function extractErrorString(e: string) {
    if (e.startsWith('Fault') && e.includes('msg=')) {
        const msgIndex = e.indexOf('msg=');
        const msgEndIndex = e.indexOf(')', msgIndex);
        return e.substring(msgIndex + 4, msgEndIndex);
    }

    return e;
}

export function getQuery(activatedRoute: ActivatedRoute, name: string): string | null {
    return activatedRoute.snapshot.queryParamMap.get(name);
}

export function getParam(activatedRoute: ActivatedRoute, name: string): string | null {
    return activatedRoute.snapshot.paramMap.get(name);
}

function options(...values: string[]): Option[] {
    return values.map(v => ({label: v.toString(), value: v}));
}

export function optionBasics(...values: string[]): Option[] {
    return values.map(v => ({label: v.toString(), value: v}));
}

export function optionsBoolean(): Option[] {
    return [
        {label: 'Yes', value: true},
        {label: 'No', value: false},
    ];
}

export function findModifiedList<T>(list: T[], originalList: T[]): T[] {
    return list.filter((obj, index) => !isEqual(obj, originalList[index]));
}

function selectItems(values: any[]): SelectItem[] {
    return values.map(v => ({label: v.toString(), value: v}));
}

function selectItemYears(from: number, to: number) {
    const thisYear = new Date().getFullYear();
    const start = thisYear + from;
    const end = thisYear + to;

    const years: SelectItem[] = [];
    for (let i = start; i <= end; i++) {
        years.push({label: i.toString(), value: i});
    }

    return years;
}

function getArrayFromDate(from: Date, to: Date, fmt = 'yyyy-MM-dd'): HeaderItem[] {
    const headerList = [];
    const dayDiff = differenceInCalendarDays(to, from);

    for (let i = 0; i <= dayDiff; i++) {
        const thisDate = addDays(from, i);
        headerList.push({
            date: thisDate,
            label: lightFormat(thisDate, fmt),
            dow: format(thisDate, 'EEE'),
            isToday: isToday(thisDate),
            isWeekEnd: isWeekend(thisDate),
        });
    }
    return headerList;
}

function validateHhMm(value: string): boolean {
    return /^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(:[0-5][0-9])?$/.test(value);
}


export const helpers = {
    getQuery,
    getParam,
    selectItemYears,
    selectItems,
    options,
    optionsBoolean,
    dateList: getArrayFromDate,
    validateHhMm
};

